import type { ReactNode } from "react";
import { AlertTriangleIcon, SearchXIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { TableCell, TableRow } from "~/components/ui/table";

/**
 * The three things a table shows instead of rows, as rows.
 *
 * Rendered inside the <tbody> rather than replacing the table so the header and
 * the filter bar above it stay put. A table that collapses to a centred spinner
 * and then springs back to full height makes every filter change feel like a
 * page load.
 */

export function TableSkeleton({ columns, rows = 8 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, row) => (
        <TableRow key={row}>
          {Array.from({ length: columns }, (_, column) => (
            <TableCell key={column}>
              <Skeleton className="h-4 w-full max-w-[10rem]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/**
 * An empty result. Deliberately not styled as a failure: a filter that matches
 * nothing is a normal, correct answer, and dressing it in warning colours sends
 * people looking for a bug that is not there.
 */
export function TableEmpty({
  columns,
  message,
  onClear,
}: {
  columns: number;
  message: ReactNode;
  /** Omitted when no filter is applied -- there is then nothing to clear. */
  onClear?: () => void;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columns} className="h-40 whitespace-normal text-center">
        <div className="flex flex-col items-center gap-3">
          <SearchXIcon className="size-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{message}</p>
          {onClear ? (
            <Button variant="outline" size="sm" onClick={onClear}>
              Clear filters
            </Button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

/** A failed load, with the server's own message and a retry. */
export function TableError({
  columns,
  message,
  onRetry,
}: {
  columns: number;
  message: string;
  onRetry: () => void;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columns} className="h-40 whitespace-normal text-center">
        <div className="flex flex-col items-center gap-3">
          <AlertTriangleIcon className="size-6 text-destructive" />
          <p className="max-w-md text-sm text-muted-foreground">{message}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
